import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const PropertySuccessPage = ({ listingData, onBoost, onShare }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    views: 0,
    leads: 0,
    favorites: 0
  });

  const shareOptions = [
    {
      id: 'facebook',
      name: 'Facebook',
      icon: '📘',
      color: 'from-blue-600 to-blue-700'
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp',
      icon: '💬',
      color: 'from-green-600 to-green-700'
    },
    {
      id: 'tiktok',
      name: 'TikTok',
      icon: '🎵',
      color: 'from-pink-600 to-purple-600'
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: '📷',
      color: 'from-purple-600 to-pink-600'
    },
    {
      id: 'copy',
      name: 'Link másolása',
      icon: '🔗',
      color: 'from-gray-600 to-gray-700'
    }
  ];

  const handleShare = (platform) => {
    const url = `https://hopphomes.hu/listing/${listingData?.id}`;
    const text = `Nézd meg ezt az ingatlant: ${listingData?.title}`;

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
        break;
      case 'tiktok':
        alert('TikTok megosztás hamarosan elérhető!');
        break;
      case 'instagram':
        alert('Instagram megosztás hamarosan elérhető!');
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        alert('Link másolva a vágólapra!');
        break;
      default:
        break;
    }

    if (onShare) onShare(platform);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Animation */}
        <div className="text-center mb-8">
          <div className="inline-block animate-bounce mb-4">
            <div className="text-8xl">🎉</div>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
            Gratulálunk! 🚀
          </h1>
          <p className="text-xl text-gray-300">
            Az ingatlanod sikeresen feltöltve és élő!
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 backdrop-blur-xl rounded-2xl p-6 border-2 border-blue-500/30 text-center">
            <div className="text-4xl font-black text-white mb-2">{stats.views}</div>
            <div className="text-gray-300 text-sm">Megtekintés</div>
          </div>
          <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 backdrop-blur-xl rounded-2xl p-6 border-2 border-purple-500/30 text-center">
            <div className="text-4xl font-black text-white mb-2">{stats.leads}</div>
            <div className="text-gray-300 text-sm">Érdeklődő</div>
          </div>
          <div className="bg-gradient-to-br from-orange-500/20 to-red-500/20 backdrop-blur-xl rounded-2xl p-6 border-2 border-orange-500/30 text-center">
            <div className="text-4xl font-black text-white mb-2">{stats.favorites}</div>
            <div className="text-gray-300 text-sm">Kedvenc</div>
          </div>
        </div>

        {/* Listing Preview */}
        {listingData && (
          <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-6 border-2 border-white/[0.08] mb-8">
            <h3 className="text-xl font-black text-white mb-4">📋 Hirdetésed előnézete</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-gray-400 text-sm mb-1">Cím</p>
                <p className="text-white font-semibold">{listingData.title || 'N/A'}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm mb-1">Ár</p>
                <p className="text-white font-semibold">{listingData.price ? `${parseInt(listingData.price).toLocaleString()} Ft` : 'N/A'}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm mb-1">Alapterület</p>
                <p className="text-white font-semibold">{listingData.size || 'N/A'} m²</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm mb-1">Szobák</p>
                <p className="text-white font-semibold">{listingData.rooms || 'N/A'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Boost CTA */}
        <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 backdrop-blur-xl rounded-2xl p-6 border-2 border-orange-500/30 mb-8">
          <div className="flex items-start gap-4">
            <div className="text-4xl">🔥</div>
            <div className="flex-1">
              <h3 className="text-xl font-black text-white mb-2">
                Gyorsítsd az eladást!
              </h3>
              <p className="text-gray-300 mb-4">
                A kiemelt hirdetések átlagosan <strong className="text-white">3x több érdeklődőt</strong> érnek el és 
                <strong className="text-white"> 60%-kal gyorsabban</strong> kelnek el.
              </p>
              <button
                onClick={onBoost}
                className="bg-gradient-to-r from-[#e31937] to-red-600 hover:from-red-600 hover:to-[#e31937] text-white font-black px-6 py-3 rounded-xl shadow-xl hover:shadow-2xl hover:shadow-red-500/50 transition-all"
              >
                Kiemelés most! 🚀
              </button>
            </div>
          </div>
        </div>

        {/* Share Section */}
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-6 border-2 border-white/[0.08] mb-8">
          <h3 className="text-xl font-black text-white mb-4 text-center">
            📢 Oszd meg a hirdetésedet!
          </h3>
          <p className="text-gray-300 text-center mb-6">
            Minél többen látják, annál gyorsabban eladhatod
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {shareOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => handleShare(option.id)}
                className={`bg-gradient-to-br ${option.color} hover:scale-105 text-white font-bold py-4 rounded-xl transition-all shadow-lg hover:shadow-xl`}
              >
                <div className="text-3xl mb-1">{option.icon}</div>
                <div className="text-xs">{option.name}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-gradient-to-br from-blue-500/20 to-purple-500/20 backdrop-blur-xl rounded-2xl p-6 border-2 border-blue-500/30 mb-8">
          <h3 className="text-xl font-black text-white mb-4">🎯 Következő lépések</h3>
          <ul className="space-y-3">
            <li className="flex items-center gap-3 text-gray-300">
              <span className="text-green-500 text-xl">✓</span>
              <span>Ellenőrizd az e-mailedet - küldtünk egy visszaigazolást</span>
            </li>
            <li className="flex items-center gap-3 text-gray-300">
              <span className="text-yellow-500 text-xl">⏳</span>
              <span>Érdeklődők hamarosan érkezni fognak</span>
            </li>
            <li className="flex items-center gap-3 text-gray-300">
              <span className="text-blue-500 text-xl">📊</span>
              <span>Kövesd a statisztikákat a dashboardon</span>
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-cyan-600 hover:to-blue-600 text-white font-black py-4 rounded-xl shadow-xl hover:shadow-2xl transition-all"
          >
            📊 Dashboard
          </button>
          <button
            onClick={() => navigate('/elado-ingatlanok')}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-pink-600 hover:to-purple-600 text-white font-black py-4 rounded-xl shadow-xl hover:shadow-2xl transition-all"
          >
            🏠 Összes ingatlan
          </button>
        </div>

        {/* Support */}
        <div className="text-center mt-8">
          <p className="text-gray-400 text-sm mb-2">
            Kérdésed van? Segítünk!
          </p>
          <div className="flex justify-center gap-4">
            <a
              href="tel:+36304130001"
              className="text-[#e31937] hover:text-red-600 font-semibold transition-colors"
            >
              📞 +36 30 413 0001
            </a>
            <a
              href="mailto:hoppxplatform@gmail.com"
              className="text-[#e31937] hover:text-red-600 font-semibold transition-colors"
            >
              ✉️ Email
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertySuccessPage;
